/**
 * 企业大脑 — 认证状态管理 (Zustand)
 * 游客模式不持久化，刷新页面需重新登录
 */
import { create } from 'zustand';
import { login as apiLogin, guestLogin as apiGuestLogin, type LoginResponse } from '@/services/api';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  tenant_id?: string;
  avatar_color?: string;
  avatar_text?: string;
  avatar_url?: string;
  phone?: string;
}

interface AuthState {
  token: string | null;
  user: User | null;
  tenantId: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<User>;
  guestLogin: () => Promise<User>;
  logout: () => void;
  setAuth: (token: string, user: User) => void;
  setUser: (user: User) => void;
  getTenantPath: () => string;
}

const getStoredUser = (): User | null => {
  try {
    const stored = localStorage.getItem('eb-user');
    if (!stored) return null;
    const user = JSON.parse(stored);
    // 游客用户不从 localStorage 恢复
    if (user.role === 'guest') {
      localStorage.removeItem('eb-token');
      localStorage.removeItem('eb-user');
      localStorage.removeItem('eb-tenant-id');
      return null;
    }
    return user;
  } catch {
    return null;
  }
};

const getStoredToken = (): string | null => {
  try {
    const user = getStoredUser();
    // 如果用户是游客或不存在，清除 token
    if (!user) {
      localStorage.removeItem('eb-token');
      return null;
    }
    return localStorage.getItem('eb-token');
  } catch {
    return null;
  }
};

const getStoredTenantId = (): string | null => {
  try {
    const user = getStoredUser();
    if (!user) return null;
    return user.tenant_id || localStorage.getItem('eb-tenant-id') || null;
  } catch {
    return null;
  }
};

export const useAuthStore = create<AuthState>((set, get) => ({
  token: getStoredToken(),
  user: getStoredUser(),
  tenantId: getStoredTenantId(),
  isAuthenticated: !!getStoredToken(),

  login: async (email: string, password: string) => {
    const data: LoginResponse = await apiLogin(email, password);
    const tenantId = data.user.tenant_id || 'default';
    
    // 正常用户登录，保存到 localStorage
    localStorage.setItem('eb-token', data.token);
    localStorage.setItem('eb-user', JSON.stringify(data.user));
    localStorage.setItem('eb-tenant-id', tenantId);
    
    set({ 
      token: data.token, 
      user: data.user, 
      tenantId,
      isAuthenticated: true 
    });
    
    return data.user;
  },

  guestLogin: async () => {
    // 从 URL 路径获取租户 ID
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const nonTenantPaths = ['login', 'apps', 'tools', 'wins', 'knowledge', 'connectors', 'monitor-tasks', 'webhooks', 'skillhub', 'sessions', 'docs', 'org', 'api'];
    let urlTenantId: string | undefined;
    if (pathParts[0] && !nonTenantPaths.includes(pathParts[0])) {
      urlTenantId = pathParts[0];
    }
    
    const data: LoginResponse = await apiGuestLogin(urlTenantId);
    const tenantId = data.user.tenant_id || urlTenantId || 'default';
    
    // 游客登录：保存到 localStorage 以便 API 请求能获取 token
    // 但在页面初始化时会被清除（getStoredUser 会检查 role）
    localStorage.setItem('eb-token', data.token);
    localStorage.setItem('eb-user', JSON.stringify(data.user));
    localStorage.setItem('eb-tenant-id', tenantId);
    
    set({ 
      token: data.token, 
      user: data.user, 
      tenantId,
      isAuthenticated: true 
    });
    
    return data.user;
  },

  logout: () => {
    localStorage.removeItem('eb-token');
    localStorage.removeItem('eb-user');
    localStorage.removeItem('eb-tenant-id');
    set({ token: null, user: null, tenantId: null, isAuthenticated: false });
  },

  setAuth: (token: string, user: User) => {
    const tenantId = user.tenant_id || 'default';
    // 只有非游客用户才保存到 localStorage
    if (user.role !== 'guest') {
      localStorage.setItem('eb-token', token);
      localStorage.setItem('eb-user', JSON.stringify(user));
      localStorage.setItem('eb-tenant-id', tenantId);
    }
    set({ token, user, tenantId, isAuthenticated: true });
  },

  setUser: (user: User) => {
    const tenantId = user.tenant_id || 'default';
    if (user.role !== 'guest') {
      localStorage.setItem('eb-user', JSON.stringify(user));
      localStorage.setItem('eb-tenant-id', tenantId);
    }
    set({ user, tenantId });
  },

  getTenantPath: () => {
    const { tenantId } = get();
    if (!tenantId || tenantId === 'default') {
      return '';
    }
    return `/${tenantId}`;
  },
}));
